import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";

interface Faction {
  id: string;
  slug: string;
  name: string;
  color: string;
  position: number;
}

interface ListResp {
  factions: Faction[];
}

const QK = ["dashboard-factions"];

export default function DashboardFactions(): JSX.Element {
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: QK,
    queryFn: () => api.get<ListResp>("/api/streamer/factions"),
    enabled: !!user,
  });

  const createMut = useMutation({
    mutationFn: (body: { slug: string; name: string; color: string }) =>
      api.post<{ faction: Faction }>("/api/streamer/factions", body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ListResp>(QK);
      const optimistic: Faction = {
        id: `tmp-${Date.now()}`,
        slug: body.slug,
        name: body.name,
        color: body.color,
        position: (prev?.factions.length ?? 0),
      };
      qc.setQueryData<ListResp>(QK, (old) => ({
        factions: [...(old?.factions ?? []), optimistic],
      }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(QK, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const patchMut = useMutation({
    mutationFn: (body: { id: string; name?: string; color?: string; position?: number }) =>
      api.patch<{ ok: true }>("/api/streamer/factions", body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ListResp>(QK);
      qc.setQueryData<ListResp>(QK, (old) => ({
        factions: (old?.factions ?? []).map((f) =>
          f.id === body.id ? { ...f, ...body } : f,
        ),
      }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(QK, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/streamer/factions?id=${encodeURIComponent(id)}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData<ListResp>(QK);
      qc.setQueryData<ListResp>(QK, (old) => ({
        factions: (old?.factions ?? []).filter((f) => f.id !== id),
      }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(QK, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });

  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#7C4DFF");
  const [createError, setCreateError] = useState<string | null>(null);

  if (loading) return <p>Loading…</p>;
  if (!user) return <Navigate to="/login?returnUrl=/dashboard/factions" replace />;

  const onCreate = (e: React.FormEvent): void => {
    e.preventDefault();
    setCreateError(null);
    if (!newSlug || !newName) {
      setCreateError("Slug and name required");
      return;
    }
    createMut.mutate(
      { slug: newSlug, name: newName, color: newColor },
      {
        onSuccess: () => {
          setNewSlug("");
          setNewName("");
          setNewColor("#7C4DFF");
        },
        onError: (e) => setCreateError(String(e)),
      },
    );
  };

  return (
    <>
      <h1>Factions</h1>
      <p style={{ color: "var(--text-sec)", marginTop: -8 }}>
        Donors pick a faction when they drop a totem. Edit colors and order to match your channel.
      </p>

      <div className="card stack">
        <h2 style={{ marginTop: 0 }}>Add faction</h2>
        <form onSubmit={onCreate} className="dfc-form">
          <input
            className="dfc-input"
            placeholder="slug (e.g. nova)"
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            maxLength={24}
          />
          <input
            className="dfc-input"
            placeholder="Display name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={60}
          />
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="dfc-color"
          />
          <button className="btn btn-primary" type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? "Adding…" : "Add"}
          </button>
        </form>
        {createError ? (
          <p style={{ color: "#FF5252", marginTop: 4, fontSize: 13 }}>{createError}</p>
        ) : null}
      </div>

      <div className="card stack">
        <h2 style={{ marginTop: 0 }}>Your factions</h2>
        {q.isLoading ? (
          <p style={{ color: "var(--text-mute)" }}>Loading…</p>
        ) : (q.data?.factions ?? []).length === 0 ? (
          <p style={{ color: "var(--text-mute)" }}>None yet — add one above.</p>
        ) : (
          <div className="dfc-list">
            {[...(q.data?.factions ?? [])]
              .sort((a, b) => a.position - b.position)
              .map((f) => (
                <FactionRow
                  key={f.id}
                  faction={f}
                  onPatch={(patch) => patchMut.mutate({ id: f.id, ...patch })}
                  onDelete={() => {
                    if (confirm(`Delete faction "${f.name}"? Existing donations keep their faction id.`)) {
                      deleteMut.mutate(f.id);
                    }
                  }}
                />
              ))}
          </div>
        )}
      </div>

      <style>{`
        .dfc-form { display: grid; grid-template-columns: 1fr 1fr 56px auto; gap: 10px; align-items: center; }
        .dfc-input { padding: 8px 10px; background: var(--bg-card); border: 1px solid var(--line); color: var(--text-pri, #fff); border-radius: 6px; font: inherit; font-size: 13px; }
        .dfc-color { width: 56px; height: 36px; background: transparent; border: 1px solid var(--line); border-radius: 6px; padding: 2px; cursor: pointer; }
        .dfc-list { display: flex; flex-direction: column; gap: 8px; }
        .dfc-row { display: grid; grid-template-columns: 36px 1fr 1fr 56px 80px 80px; gap: 10px; align-items: center; padding: 10px; border: 1px solid var(--line); border-radius: 6px; background: var(--bg-card); }
        .dfc-dot { width: 24px; height: 24px; border-radius: 50%; box-shadow: 0 0 8px currentColor; }
        .dfc-slug { font-family: var(--f-mono); font-size: 11px; color: var(--text-mute); letter-spacing: .1em; text-transform: uppercase; }
        @media (max-width: 700px) { .dfc-form { grid-template-columns: 1fr; } .dfc-row { grid-template-columns: 36px 1fr 56px; row-gap: 6px; } }
      `}</style>
    </>
  );
}

function FactionRow({
  faction,
  onPatch,
  onDelete,
}: {
  faction: Faction;
  onPatch: (patch: { name?: string; color?: string; position?: number }) => void;
  onDelete: () => void;
}): JSX.Element {
  const [name, setName] = useState(faction.name);
  const [color, setColor] = useState(faction.color);

  return (
    <div className="dfc-row" style={{ borderColor: color }}>
      <div className="dfc-dot" style={{ background: color, color }} />
      <div>
        <input
          className="dfc-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name !== faction.name && onPatch({ name })}
          maxLength={60}
        />
        <div className="dfc-slug">@{faction.slug}</div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          onBlur={() => color !== faction.color && onPatch({ color })}
          className="dfc-color"
        />
        <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--text-mute)" }}>
          {color.toUpperCase()}
        </span>
      </div>
      <input
        type="number"
        className="dfc-input"
        value={faction.position}
        onChange={(e) => onPatch({ position: parseInt(e.target.value, 10) || 0 })}
        style={{ width: 56 }}
        title="position"
      />
      <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--text-mute)" }}>
        order
      </span>
      <button className="btn" onClick={onDelete} style={{ color: "#FF5252" }}>
        Delete
      </button>
    </div>
  );
}
